import React, { useState, Fragment, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import { Link, useNavigate } from "react-router-dom";
import {
  SearchIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/solid";
import {
  PlusCircleIcon,
  XIcon,
  UserIcon,
  OfficeBuildingIcon,
  IdentificationIcon,
  HomeIcon,
  CreditCardIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/outline";
import { Transition, Dialog } from "@headlessui/react";
import ActionDropdown from "../../components/ActionDropdown";
import fire from "./../../utils/Toastr";
import ThirdPartyServices from "../../services/ThirdParty";
import { HollowDotsSpinner } from "react-epic-spinners";
import ConfirmModal from "../../components/Modals/ConfirmModal";
import Paginate from "../../utils/Paginate";

export default function ThirdPartyOrganizations() {
  const navigate = useNavigate();
  const [thirdParty, setThirdParty] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // State for confirmation modals
  const [showOnboardModal, setShowOnboardModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [onboardAccountNumber, setOnboardAccountNumber] = useState("");
  const [onboardTenantId, setOnboardTenantId] = useState("");

  // State for view organization modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewOrgLoading, setViewOrgLoading] = useState(false);
  const [organizationDetails, setOrganizationDetails] = useState(null);

  // Pagination states
  const [pageIndex, setPageIndex] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  useEffect(() => {
    fetchOrganizations(1);
  }, []);

  const fetchOrganizations = async (page = 1) => {
    setLoading(true);
    try {
      const response = await ThirdPartyServices.listOrganizations(page);

      if (response.status && response.data && response.data.result) {
        setThirdParty(response.data.result);
        setPageIndex(response.data.pageIndex || page);
        setTotalPages(response.data.totalPages || 1);
        setTotalCount(response.data.totalCount || 0);
        setHasNextPage(response.data.hasNextPage || false);
        setHasPreviousPage(response.data.hasPreviousPage || false);
      } else if (response.organizations) {
        setThirdParty(response.organizations);
        setTotalCount(response.organizations.length || 0);
      } else if (Array.isArray(response.data)) {
        setThirdParty(response.data);
        setTotalCount(response.data.length || 0);
      } else {
        setError("Unexpected response structure");
        fire("error", "Failed to load organizations");
      }
    } catch (err) {
      setError("Failed to fetch organizations");
      fire("error", "Failed to fetch organizations");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch single organization details
  const fetchOrganizationDetails = async (
    PremiumMobileUserId,
    OrganisationId,
  ) => {
    setViewOrgLoading(true);
    try {
      const response = await ThirdPartyServices.getOrganization(
        PremiumMobileUserId,
        OrganisationId,
      );

      if (response.status && response.data) {
        setOrganizationDetails(response.data);
        setShowViewModal(true);
      } else {
        fire("error", "Failed to fetch organization details");
      }
    } catch (err) {
      fire("error", "Failed to fetch organization details");
      console.error("Error fetching organization:", err);
    } finally {
      setViewOrgLoading(false);
    }
  };

  // Handle view organization click
  const handleViewOrganization = (org) => {
    setSelectedOrg(org);

    const premiumMobileUserId =
      org.premiumMobileUserId || org.userId || org.PremiumMobileUserId;
    const organisationId =
      org.organisationId || org.organizationId || org.OrganisationId;

    if (premiumMobileUserId && organisationId) {
      fetchOrganizationDetails(premiumMobileUserId, organisationId);
    } else {
      // If IDs are not available, show basic info in modal
      setOrganizationDetails(org);
      setShowViewModal(true);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) {
      fetchOrganizations(1);
      return;
    }

    setLoading(true);
    try {
      const response = await ThirdPartyServices.fetchOrganization(searchTerm);
      if (
        response.status &&
        response.data &&
        response.data.organizationDetails
      ) {
        setThirdParty([response.data.organizationDetails]);
        setTotalCount(1);
        setTotalPages(1);
        setPageIndex(1);
      } else if (response.status && response.data && response.data.result) {
        // If search returns the same structure as list
        setThirdParty(response.data.result);
        setPageIndex(response.data.pageIndex || 1);
        setTotalPages(response.data.totalPages || 1);
        setTotalCount(response.data.totalCount || response.data.result.length);
        setHasNextPage(response.data.hasNextPage || false);
        setHasPreviousPage(response.data.hasPreviousPage || false);
      } else {
        fire("info", "No organization found with that search parameter");
        setThirdParty([]);
        setTotalCount(0);
        setTotalPages(1);
      }
    } catch (err) {
      fire("error", "Search failed");
      console.error("Search error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSearchTerm("");
    fetchOrganizations(1);
  };

  // Handle onboard confirmation
  const handleOnboardClick = async (org) => {
    setSelectedOrg(org);
    setOnboardAccountNumber(org.accountNumber || "");
    setShowOnboardModal(true);

    try {
      const appResponse = await ThirdPartyServices.listApplications(1, 100);
      if (appResponse.status && appResponse.data?.result) {
        const match = appResponse.data.result.find(
          app => app.applicationName === org.applicationName
        );
        setOnboardTenantId(match?.tenantId || "");
      }
    } catch (err) {
      console.error("Error fetching tenantId:", err);
      setOnboardTenantId("");
    }
  };

  const handleOnboardConfirm = async () => {
    if (!selectedOrg) return;

    if (!onboardAccountNumber.trim()) {
      fire("error", "Account number is required");
      return;
    }
    if (!/^\d+$/.test(onboardAccountNumber.trim())) {
      fire("error", "Account number must be numeric");
      return;
    }

    setActionLoading(true);
    try {
      const response = await ThirdPartyServices.onboardOrganization({
        organizationId: selectedOrg.organizationId,
        accountNumber: onboardAccountNumber.trim(),
        applicationName: selectedOrg.applicationName,
        tenantId: onboardTenantId,
      });

      if (response.status) {
        fire("success", "Organization onboarded successfully");
        fetchOrganizations(pageIndex);
      } else {
        fire("error", response.message || "Onboarding failed");
      }
    } catch (err) {
      fire("error", "Onboarding failed");
      console.error("Onboarding error:", err);
    } finally {
      setActionLoading(false);
      setShowOnboardModal(false);
      setSelectedOrg(null);
      setOnboardAccountNumber("");
      setOnboardTenantId("");
    }
  };

  // Handle notification confirmation
  const handleNotificationClick = (org) => {
    setSelectedOrg(org);
    setShowNotificationModal(true);
  };

  const handleNotificationConfirm = async () => {
    if (!selectedOrg) return;

    setActionLoading(true);
    try {
      const response = await ThirdPartyServices.sendAccountNotification({
        applicationName: selectedOrg.applicationName,
        companyName: selectedOrg.companyName,
        companyUniqueIdentifier: selectedOrg.companyUniqueIdentifier,
        address: selectedOrg.address,
        status: "Success",
        accountNumber: selectedOrg.accountNumber,
        reason: "Account successfully opened",
      });

      if (response.status) {
        fire("success", "Notification sent successfully");
      } else {
        fire("error", response.message || "Failed to send notification");
      }
    } catch (err) {
      fire("error", "Failed to send notification");
      console.error("Notification error:", err);
    } finally {
      setActionLoading(false);
      setShowNotificationModal(false);
      setSelectedOrg(null);
    }
  };

  // Update the page change handler
  const handlePageChange = (newPage) => {
    setPageIndex(newPage);
    fetchOrganizations(newPage);
  };

  return (
    <Layout
      title={"Third-Party Organizations"}
      current={"thirdparty"}
      pageTag="thirdparty"
    >
      <React.Fragment>
        <main>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
            <div className="px-4 sm:px-6 lg:px-8">
              <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto flex">
                  <div className="flex">
                    <form onSubmit={handleSearch} className="flex">
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <SearchIcon
                            className="h-5 w-5 text-gray-400"
                            aria-hidden="true"
                          />
                        </div>
                        <input
                          type="text"
                          name="search"
                          id="search"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                          placeholder="Search organizations..."
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                          <div className="tooltip">
                            <QuestionMarkCircleIcon className="w-4 mr-1.5 text-gray-400" />
                            <span className="tooltiptext">
                              Search by company name, application name,
                              identifier or account number
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-1 ml-4">
                        <button
                          type="submit"
                          className="hover:text-white active:text-white inline-flex mr-2 items-center justify-center rounded-md border border-transparent btn-green px-6 py-2 text-sm font-medium text-green shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
                        >
                          Search
                        </button>
                        {searchTerm && (
                          <button
                            type="button"
                            onClick={handleReset}
                            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-red-700 px-6 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
                          >
                            Reset
                          </button>
                        )}
                      </div>
                    </form>
                  </div>
                </div>

                <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
                  <Link to="/thirdparty/organizations/create">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-md border border-transparent btn-primary px-2 py-2 text-sm font-medium text-white shadow-sm sm:w-auto"
                    >
                      <PlusCircleIcon className="w-4 mr-1.5" />
                      Create ThirdParty Organization
                    </button>
                  </Link>
                </div>
              </div>

              {loading ? (
                <div className="mt-8 flex justify-center">
                  <HollowDotsSpinner color="#4A90E2" />
                </div>
              ) : error ? (
                <div className="mt-8 text-red-500">{error}</div>
              ) : thirdParty.length === 0 ? (
                <div className="mt-8 text-center text-gray-500">
                  No organizations found.
                </div>
              ) : (
                <>
                  <div className="mt-8 flow-root">
                    <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                      <div className="inline-block min-w-full py-2 align-middle">
                        <div className="ring-1 ring-black ring-opacity-5 md:rounded-lg">
                          <table className="min-w-full divide-y divide-gray-300">
                            <thead className="bg-gray-50">
                              <tr>
                                <th
                                  scope="col"
                                  className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900"
                                >
                                  Application Name
                                </th>
                                <th
                                  scope="col"
                                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                >
                                  Company Name
                                </th>
                                <th
                                  scope="col"
                                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                >
                                  Unique Identifier
                                </th>
                                <th
                                  scope="col"
                                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                >
                                  Address
                                </th>
                                <th
                                  scope="col"
                                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                >
                                  Account Number
                                </th>
                                <th
                                  scope="col"
                                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                >
                                  Status
                                </th>
                                <th
                                  scope="col"
                                  className="relative py-3.5 pl-3 pr-4 sm:pr-6"
                                >
                                  <span className="sr-only">Actions</span>
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                              {thirdParty.map((org, index) => (
                                <tr key={index}>
                                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                    {org.applicationName}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                    {org.companyName}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                    {org.companyUniqueIdentifier}
                                  </td>
                                  <td className="px-3 py-4 text-sm text-gray-500">
                                    {org.address}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                    {org.accountNumber || "N/A"}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                                    <span
                                      className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                                        org.isOnboarded
                                          ? "bg-green-100 text-green-800"
                                          : "bg-yellow-100 text-yellow-800"
                                      }`}
                                    >
                                      {org.isOnboarded
                                        ? "Onboarded"
                                        : "Pending"}
                                    </span>
                                  </td>
                                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                    <ActionDropdown
                                      dropdownWidth="w-56"
                                      items={[
                                        { label: "View Details", onClick: () => handleViewOrganization(org) },
                                        { label: "Edit", onClick: () => navigate(`/thirdparty/organizations/edit/${org.organizationId}?search=${encodeURIComponent(org.applicationName || org.companyName || "")}`, { state: { organization: org } }) },
                                        ...(!org.isOnboarded
                                          ? [{ label: "Onboard Organization", onClick: () => handleOnboardClick(org) }]
                                          : []),
                                        { label: "Send Account Notification", onClick: () => handleNotificationClick(org) },
                                      ]}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pagination Component */}
                  {totalPages > 1 && (
                    <Paginate
                      nextPage={hasNextPage}
                      pageIndex={pageIndex}
                      totalCount={totalCount}
                      prevPage={hasPreviousPage}
                      totalPages={totalPages}
                      setPageIndex={handlePageChange}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </main>

        {/* View Organization Modal */}
        <Transition appear show={showViewModal} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50"
            onClose={() => {
              setShowViewModal(false);
              setOrganizationDetails(null);
            }}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <div className="flex justify-between items-start">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-medium leading-6 text-gray-900"
                      >
                        Organization Details
                      </Dialog.Title>
                      <button
                        type="button"
                        className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                        onClick={() => {
                          setShowViewModal(false);
                          setOrganizationDetails(null);
                        }}
                      >
                        <XIcon className="h-6 w-6" />
                      </button>
                    </div>

                    {viewOrgLoading ? (
                      <div className="mt-4 flex justify-center">
                        <HollowDotsSpinner color="#4A90E2" size={40} />
                      </div>
                    ) : organizationDetails ? (
                      <div className="mt-4">
                        {/* Organization Info */}
                        <div className="bg-gray-50 rounded-lg p-4 mb-4">
                          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                            <InformationCircleIcon className="h-5 w-5 mr-2 text-blue-500" />
                            Basic Information
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex items-start">
                              <OfficeBuildingIcon className="h-5 w-5 mr-2 text-gray-400 mt-0.5" />
                              <div>
                                <p className="text-xs font-medium text-gray-500">
                                  Company Name
                                </p>
                                <p className="text-sm text-gray-900">
                                  {organizationDetails.companyName || "N/A"}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-start">
                              <UserIcon className="h-5 w-5 mr-2 text-gray-400 mt-0.5" />
                              <div>
                                <p className="text-xs font-medium text-gray-500">
                                  Application Name
                                </p>
                                <p className="text-sm text-gray-900">
                                  {organizationDetails.applicationName || "N/A"}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-start">
                              <IdentificationIcon className="h-5 w-5 mr-2 text-gray-400 mt-0.5" />
                              <div>
                                <p className="text-xs font-medium text-gray-500">
                                  Unique Identifier
                                </p>
                                <p className="text-sm text-gray-900">
                                  {organizationDetails.companyUniqueIdentifier ||
                                    "N/A"}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-start">
                              <CreditCardIcon className="h-5 w-5 mr-2 text-gray-400 mt-0.5" />
                              <div>
                                <p className="text-xs font-medium text-gray-500">
                                  Account Number
                                </p>
                                <p className="text-sm text-gray-900">
                                  {organizationDetails.accountNumber || "N/A"}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-start md:col-span-2">
                              <HomeIcon className="h-5 w-5 mr-2 text-gray-400 mt-0.5" />
                              <div>
                                <p className="text-xs font-medium text-gray-500">
                                  Address
                                </p>
                                <p className="text-sm text-gray-900">
                                  {organizationDetails.address || "N/A"}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Status and Additional Details */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="text-sm font-semibold text-gray-900 mb-3">
                              Status
                            </h4>
                            <div className="flex items-center">
                              {organizationDetails.isOnboarded ? (
                                <>
                                  <CheckCircleIcon className="h-5 w-5 mr-2 text-green-500" />
                                  <span className="text-sm font-medium text-green-700">
                                    Onboarded
                                  </span>
                                </>
                              ) : (
                                <>
                                  <XCircleIcon className="h-5 w-5 mr-2 text-yellow-500" />
                                  <span className="text-sm font-medium text-yellow-700">
                                    Pending
                                  </span>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Additional fields from API response */}
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="text-sm font-semibold text-gray-900 mb-3">
                              Additional Information
                            </h4>
                            {organizationDetails.PremiumMobileUserId && (
                              <p className="text-xs text-gray-600">
                                <span className="font-medium">
                                  Premium Mobile User ID:
                                </span>{" "}
                                {organizationDetails.PremiumMobileUserId}
                              </p>
                            )}
                            {organizationDetails.OrganisationId && (
                              <p className="text-xs text-gray-600">
                                <span className="font-medium">
                                  Organization ID:
                                </span>{" "}
                                {organizationDetails.OrganisationId}
                              </p>
                            )}
                            {organizationDetails.createdAt && (
                              <p className="text-xs text-gray-600">
                                <span className="font-medium">Created:</span>{" "}
                                {new Date(
                                  organizationDetails.createdAt,
                                ).toLocaleDateString()}
                              </p>
                            )}
                            {organizationDetails.updatedAt && (
                              <p className="text-xs text-gray-600">
                                <span className="font-medium">
                                  Last Updated:
                                </span>{" "}
                                {new Date(
                                  organizationDetails.updatedAt,
                                ).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Display any additional data from the API */}
                        {organizationDetails.otherDetails && (
                          <div className="mt-4 bg-gray-50 rounded-lg p-4">
                            <h4 className="text-sm font-semibold text-gray-900 mb-3">
                              Other Details
                            </h4>
                            <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                              {JSON.stringify(
                                organizationDetails.otherDetails,
                                null,
                                2,
                              )}
                            </pre>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="mt-4 text-center text-gray-500">
                        No organization details available.
                      </div>
                    )}

                    <div className="mt-6 flex justify-end">
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                        onClick={() => {
                          setShowViewModal(false);
                          setOrganizationDetails(null);
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Onboard Modal */}
        <Transition appear show={showOnboardModal} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50"
            onClose={() => {
              setShowOnboardModal(false);
              setSelectedOrg(null);
              setOnboardAccountNumber("");
            }}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      Onboard Organization
                    </Dialog.Title>

                    <div className="mt-4">
                      <p className="text-sm text-gray-500 mb-4">
                        Enter account number to onboard{" "}
                        <span className="font-medium text-gray-700">
                          {selectedOrg?.companyName}
                        </span>
                      </p>

                      <div>
                        <label
                          htmlFor="onboardAccount"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Account Number *
                        </label>
                        <input
                          type="text"
                          id="onboardAccount"
                          value={onboardAccountNumber}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^\d*$/.test(val)) {
                              setOnboardAccountNumber(val);
                            }
                          }}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm"
                          placeholder="Enter account number"
                          disabled={actionLoading}
                        />
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowOnboardModal(false);
                          setSelectedOrg(null);
                          setOnboardAccountNumber("");
                        }}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        disabled={actionLoading}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleOnboardConfirm}
                        disabled={actionLoading}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                      >
                        {actionLoading ? (
                          <>
                            <HollowDotsSpinner
                              color="#ffffff"
                              size={20}
                              className="mr-2"
                            />
                            Processing...
                          </>
                        ) : (
                          "Onboard"
                        )}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Notification Confirmation Modal */}
        <ConfirmModal
          isOpen={showNotificationModal}
          onClose={() => {
            setShowNotificationModal(false);
            setSelectedOrg(null);
          }}
          onConfirm={handleNotificationConfirm}
          title="Send Account Notification"
          message={`Send account opening notification to ${selectedOrg?.companyName}?`}
          confirmText={actionLoading ? "Sending..." : "Yes, Send"}
          cancelText="Cancel"
        />
      </React.Fragment>
    </Layout>
  );
}
